import { useQuery, type QueryClient } from "@tanstack/react-query";
import { useAuthStoreV1 } from "@/stores/authStore_v1";
import { getRunDetail, isWordChar } from "@/services/multi-agent";
import type { RunDetail } from "@/services/multi-agent";

export const multiAgentRunDetailKeys = {
  all: ["multi-agent", "run-detail"] as const,
  detail: (runId: string, orgId: string | null, userId: string | null) =>
    [...multiAgentRunDetailKeys.all, orgId ?? "", userId ?? "", runId] as const,
};

function resolveMultiAgentTenantIds(): { orgId: string | null; userId: string | null } {
  const state = useAuthStoreV1.getState();
  return {
    orgId: state.selectedOrg?.id ?? null,
    userId: state.user?.id ?? null,
  };
}

/** Whether `code` appears in `msg` as a standalone token (no surrounding word
 * char on either side). */
function hasStatusToken(msg: string, code: string): boolean {
  let from = 0;
  for (;;) {
    const idx = msg.indexOf(code, from);
    if (idx < 0) return false;
    const before = idx > 0 ? msg[idx - 1] : "";
    const after = idx + code.length < msg.length ? msg[idx + code.length] : "";
    if (!isWordChar(before) && !isWordChar(after)) return true;
    from = idx + code.length;
  }
}

/** True when a multi-agent API error indicates auth or cross-tenant access denial. */
export function isMultiAgentAuthError(err: unknown): boolean {
  const msg = (err as Error)?.message ?? String(err);
  return hasStatusToken(msg, "401") || hasStatusToken(msg, "403");
}

export async function fetchMultiAgentRunDetail(
  queryClient: QueryClient,
  runId: string,
): Promise<RunDetail> {
  const { orgId, userId } = resolveMultiAgentTenantIds();
  return queryClient.fetchQuery({
    queryKey: multiAgentRunDetailKeys.detail(runId, orgId, userId),
    queryFn: () => getRunDetail(runId),
  });
}

export type UseRunDetailOptions = {
  enabled?: boolean;
};

export type UseRunDetailResult = {
  detail: RunDetail | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
};

/**
 * Fetches a single run detail snapshot (REST /detail).
 */
export function useRunDetail(
  runId: string | null | undefined,
  options: UseRunDetailOptions = {},
): UseRunDetailResult {
  const { enabled = true } = options;
  const orgId = useAuthStoreV1((s) => s.selectedOrg?.id ?? null);
  const userId = useAuthStoreV1((s) => s.user?.id ?? null);
  const query = useQuery({
    queryKey: multiAgentRunDetailKeys.detail(runId ?? "", orgId, userId),
    queryFn: () => getRunDetail(runId!),
    enabled: Boolean(runId && orgId && userId) && enabled,
  });

  return {
    detail: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: () => {
      void query.refetch();
    },
  };
}
